import { useSelector } from 'react-redux';
import { getAnalyticsProduct } from '@tink/tink-link-core/src/analytics-product';
import { detectUserFlow } from '@tink/tink-link-core/src/analytics-user-flow';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { isDeploymentBuild } from '@tink/config/src/env';
import { ProductType } from '@tink/core/src/types';
import { useCallback } from 'react';
import {
  AnalyticsEventType,
  ApplicationEvent,
  InteractionEvent,
  ViewEvent,
} from '../../models/analytics';
import { AppState } from '../../stores/redux-store';
import { isEnumType } from '../../utilities/urlParameters';
import { TRACKING_ID } from '../../utilities/urlParametersValidator';
import { useUrlQuery } from '../../components/ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { getAnalyticsProductsFromProductType } from './tink-link-analytics';
import {
  SendAnalyticsMessage,
  sendAnalyticsMessageDev,
  sendAnalyticsMessageProd,
} from './trackers';

/* istanbul ignore next: nothing to test here */
const realSendAnalyticsMessage = isDeploymentBuild()
  ? sendAnalyticsMessageProd
  : sendAnalyticsMessageDev;

const getUserAgentData = () => {
  return {
    device: userAgent.deviceType,
    deviceVendor: userAgent.deviceVendor,
    deviceModel: userAgent.deviceModel,
    deviceOs: userAgent.deviceOs,
    deviceOsVersion: userAgent.osVersion,
    browserName: userAgent.browserName,
    browserVersion: userAgent.browserVersion,
    isWebView: userAgent.isWebView,
  };
};

export const getQueryParams = ({ pathname, href }: Location) => {
  const userFlow = detectUserFlow(href);
  // '/1.0/authorize/connect' -> ['', '1.0', 'authorize', 'connect']
  const segments = pathname.split('/');
  const version = segments[1];
  const maybeProductType = segments[2];

  const productType = isEnumType(ProductType)(maybeProductType) ? maybeProductType : undefined;

  return {
    version,
    flow: userFlow,
    sessionId: TRACKING_ID,
    product: productType ? getAnalyticsProduct(productType) : undefined,
    products: productType ? getAnalyticsProductsFromProductType(productType) : undefined,
  };
};

export const selectCredentials = (state: AppState) => {
  return state.credentials.data;
};

export const selectUser = (state: AppState) => {
  return state.user.user;
};

export const selectSession = (state: AppState) => {
  return state.session.data;
};

export const useNewAnalytics = (
  sendAnalyticsMessage: SendAnalyticsMessage = realSendAnalyticsMessage
) => {
  const urlQueryParams = useUrlQuery();

  const user = useSelector(selectUser);
  const credentials = useSelector(selectCredentials);
  const session = useSelector(selectSession);

  const getData = useCallback(() => {
    const {
      isIframe,
      market,
      clientId,
      appUri,
      providerName,
      credentialsId,
      paymentRequestId,
      credentialsIdFromTPP,
    } = urlQueryParams;

    return {
      ...getQueryParams(window.location),
      ...getUserAgentData(),
      isIframe,
      market,
      clientId,
      hasAppUri: Boolean(appUri),
      paymentRequestId,
      providerName: providerName || credentials?.providerName,
      credentialsId: credentialsId || credentialsIdFromTPP || credentials?.id,

      userId: user?.id,
      platform: 'WEB',
      timestamp: new Date().toISOString(),
      sessionSourceAccountSupplied: Boolean(session?.sourceAccountNumber),
    };
  }, [
    credentials?.id,
    credentials?.providerName,
    session?.sourceAccountNumber,
    urlQueryParams,
    user?.id,
  ]);

  const sendViewEvent = useCallback(
    (viewEvent: ViewEvent) => {
      const payload = {
        ...getData(),
        ...viewEvent,
      };

      return sendAnalyticsMessage({
        eventType: AnalyticsEventType.VIEW_EVENT,
        data: payload,
      });
    },
    [getData, sendAnalyticsMessage]
  );

  const sendInteractionEvent = useCallback(
    (interactionEvent: InteractionEvent) => {
      const payload = {
        ...getData(),
        ...interactionEvent,
      };

      return sendAnalyticsMessage({
        data: payload,
        eventType: AnalyticsEventType.INTERACTION_EVENT,
      });
    },
    [getData, sendAnalyticsMessage]
  );

  const sendApplicationEvent = useCallback(
    (applicationEvent: ApplicationEvent) => {
      const payload = {
        ...getData(),
        type: applicationEvent,
      };

      return sendAnalyticsMessage({
        data: payload,
        eventType: AnalyticsEventType.APPLICATION_EVENT,
      });
    },
    [getData, sendAnalyticsMessage]
  );

  return {
    sendViewEvent,
    sendInteractionEvent,
    sendApplicationEvent,
  };
};
